/**
 * TanStack Query client configuration
 */
import { QueryClient } from '@tanstack/react-query';

const MAX_RETRIES = 3;

/**
 * Retry network and server errors, never a 4xx: 401/403/404 do not change on a retry, and the
 * backoff only delays the sign-in redirect or the error message. apiClient rejects with
 * `{ message, status }` (services/api/client.ts).
 */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < MAX_RETRIES;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time: 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry network and server errors up to 3 times, never a 4xx
      retry: retryUnlessClientError,
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Query keys factory for type-safe query keys
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },

  // Sessions
  sessions: {
    all: ['sessions'] as const,
    lists: () => [...queryKeys.sessions.all, 'list'] as const,
    list: (filters: { category?: string; search?: string; lang?: string; session_type?: string }) =>
      [...queryKeys.sessions.lists(), filters] as const,
    details: () => [...queryKeys.sessions.all, 'detail'] as const,
    detail: (id: string, lang?: string) => [...queryKeys.sessions.details(), id, lang] as const,
    categories: () => [...queryKeys.sessions.all, 'categories'] as const,
    groups: (lang: string) => [...queryKeys.sessions.all, 'groups', lang] as const,
    history: (lang?: string) => [...queryKeys.sessions.all, 'history', lang] as const,
    statistics: () => [...queryKeys.sessions.all, 'statistics'] as const,
    recent: () => [...queryKeys.sessions.all, 'recent'] as const,
    favorites: (lang?: string) => [...queryKeys.sessions.all, 'favorites', lang] as const,
    progress: (id: string) => [...queryKeys.sessions.all, 'progress', id] as const,
  },

  // Journal (per user, so a shared device never shows another account's cached entries)
  journal: {
    all: ['journal'] as const,
    entries: (userId: string) => [...queryKeys.journal.all, 'entries', userId] as const,
  },

  // In-app book (per user: access, progress and the watermark differ per account)
  book: {
    all: ['book'] as const,
    state: (userId: string) => [...queryKeys.book.all, 'state', userId] as const,
    chapter: (userId: string, chapterId: string) => [...queryKeys.book.all, 'chapter', userId, chapterId] as const,
  },

  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
    plans: () => [...queryKeys.subscriptions.all, 'plans'] as const,
    status: () => [...queryKeys.subscriptions.all, 'status'] as const,
    features: () => [...queryKeys.subscriptions.all, 'features'] as const,
  },
} as const;

export default queryClient;
